import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { DetailsListLayoutMode, SelectionMode, DetailsRow, Text } from 'office-ui-fabric-react';
import moment from 'moment';
import { sumBy, orderBy, sortBy, toPairs, groupBy } from 'lodash';
import clsx from 'clsx';
import formatNumber from '../../utils/formatNumber';
import styles from './StatementSummary.style';
import DetailsList from '../DetailsList';

function formatDate(isoDate, dateFormat) {
  return moment.utc(isoDate).format(dateFormat);
}

function formatValue(value, format) {
  if (!format) {
    return value;
  }

  if (format.type === 'number') {
    return formatNumber(styles, value, format.maxDecimals);
  }

  if (format.type === 'date') {
    return formatDate(value, format.dateString);
  }

  return value;
}

export default function StatementSummary({ config, summary }) {
  const [sortColumn, setSortColumn] = useState(null);

  const detailsListData = useMemo(() => {
    const columns = config.summary.projection
      // only take projections which are not section headers
      .filter(p => p.format?.type !== 'sectionHeader')
      .map(projection => ({
        key: projection.to,
        name: projection.to,
        fieldName: projection.to,
        isRowHeader: true,
        isResizable: true,

        isSorted: sortColumn?.key === projection.to,
        isSortedDescending: sortColumn?.isSortedDescending,
        sortAscendingAriaLabel: 'Sorted A to Z',
        sortDescendingAriaLabel: 'Sorted Z to A',
        headerClassName:
          projection.format?.type === 'number'
            ? clsx(styles.rightAlignHeader)
            : clsx(styles.transparentBackground),
        onColumnClick(_, column) {
          const isSortedDescending = !column.isSortedDescending;

          setSortColumn({
            ...column,
            isSortedDescending,
          });
        },
        isPadded: true,
      }));

    let summaryItems = summary;

    if (sortColumn) {
      summaryItems = orderBy(
        [...summary],
        sortColumn.fieldName,
        sortColumn.isSortedDescending ? 'desc' : 'asc',
      );
    }

    const headerField = config.summary.projection.find(p => p.format?.type === 'sectionHeader');
    const sortFn = headerField ? i => i[headerField.to] : () => 0;
    const items = sortBy(summaryItems, sortFn).map(summaryItem => {
      const item = {};

      config.summary.projection.forEach(projection => {
        const { to, format } = projection;
        item[to] = formatValue(summaryItem[to], format);
      });
      return item;
    });

    const footer = {};
    const buildGroups = () => {
      if (!headerField) {
        return undefined;
      }

      const agg = sortBy(toPairs(groupBy(items, item => item[headerField.to])), ([key]) => key).map(
        ([key, l]) => [key, l.length],
      );
      const reduceGroups = ([head, ...rest], startIndex) => {
        if (head === undefined) return [];
        const [key, count] = head;
        const group = { key, name: key, startIndex, count, level: 0 };
        return [group, ...reduceGroups(rest, startIndex + count)];
      };
      return reduceGroups(agg, 0);
    };
    const groups = buildGroups();

    config.summary.projection.forEach((projection, index) => {
      const { to, format } = projection;

      if (index === 0) {
        footer[to] = 'Total Royalties Earned';
        return;
      }

      if (!format?.showTotals) {
        footer[to] = '';
        return;
      }

      const sumOfValues = sumBy(summary, to);

      footer[to] = formatValue(sumOfValues, format);
    });

    return { columns, items, footer, groups };
  }, [config, summary, sortColumn, setSortColumn]);

  return (
    <DetailsList
      className={styles.detailsList}
      items={detailsListData.items}
      columns={detailsListData.columns}
      groups={detailsListData.groups}
      selectionMode={SelectionMode.none}
      layoutMode={DetailsListLayoutMode.justified}
      isHeaderVisible
      compactRows={false}
      onRenderDetailsFooter={props => (
        <DetailsRow
          {...props}
          item={detailsListData.footer}
          itemIndex={-1}
          selectionMode={SelectionMode.none}
          onRenderItemColumn={(item, index, column) => {
            return <Text>{item[column.fieldName]}</Text>;
          }}
        />
      )}
    />
  );
}

StatementSummary.propTypes = {
  config: PropTypes.shape().isRequired,
  summary: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};
